import { NgZone, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { USERROLE } from 'src/app/shared/enums/index.enum';
import { environment } from 'src/environments/environment';

import { UserResponse, SascEmailConfigModel } from 'src/app/interfaces/user';
import { AuthSessionFacade } from '@services/auth-session/auth-session.facade';

import {
	GET_USER_MOCK, 
	POPULATE_POLICY_MANAGEMENT,
	RESET_QUERY_PARAMS,
	BACK_TO_ALPHA,
	BACK_TO_SAT,
	REDIRECT_SAT,
	SIGN_OUT,
  CLEAR_AUTH_SESSION,
	GET_USER,
  STORE_QUERY_PARAMS,
  RESTORE_AUTH_SESSION,
  REDIRECT_ALPHA_CCENTRIC,
  CROSS_SELL_ISSUE_NEW_REDIRECT_TO_ALPHA
} from './user.action';

import { UserService } from './user.service';
import { RESET_SAT_MOTOR_STATE } from '@store/sat-motor/sat-motor.action';

interface UserModel {
    user: Partial<UserResponse>,
    loading: boolean,
    role: string,
    emailConfig: SascEmailConfigModel,
    queryParams: any,
    menu: any,
}

const defaultUserState: UserModel = {
    user: null,
    loading: false,
    role: USERROLE.ADMIN,
    emailConfig: null,
    queryParams: {},
    menu: null
};

@Injectable()
@State<UserModel>({
    name: 'UserState',
    defaults: defaultUserState
})

export class UserState {
	
	constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly store: Store,
    private readonly authSessionFacade: AuthSessionFacade,
  ) { }

	@Selector()
	public static menu(state) {
		return state.menu;
	}

	@Selector()
	public static menuList(state: UserModel) {
		return state.user.menuList ? state.user.menuList : null;
	}

	@Selector()
	public static user(state: UserModel) {
		return state.user ? state.user : {
			name: "XXX",
			lastLoginDate: "XX/XX/XXXX XX:XX:XX"
		};
	}

	@Action(GET_USER)
    public getUser(
        { patchState, getState, dispatch }: StateContext<UserModel>,
        { payload }: GET_USER) {
        patchState({ loading: true });
        return this.userService
            .getUser(payload.userId)
            .pipe(
                map(res => {
                    if (res.error) {
                        this.clearClientSession(patchState);
                    } else {
                        patchState({
                            user: res,
                            loading: false,
                            role: this.getUserRole(res),
                            queryParams: payload.queryParams
                        });
                        this.authSessionFacade.setSession(res, payload.queryParams ?? {});
                    }
                }),
                catchError(err => {
                    this.clearClientSession(patchState);
                    return of(null);
                })
            );
    }

    @Action(GET_USER_MOCK)
    public getUserMock(
        { patchState, getState, dispatch }: StateContext<UserModel>,
        { payload }: any) {
        patchState({ loading: true });
        return this.userService
            .getUserMock(payload.userId)
            .pipe(
                map(res => {
                    if (res.error) {
                        this.clearClientSession(patchState);
                    } else {
                        patchState({
                            user: res,
                            loading: false,
                            role: this.getUserRole(res),
                            queryParams: payload.queryParams
                        });
                        this.authSessionFacade.setSession(res, payload.queryParams ?? {});
                    }
                }),
                catchError(err => {
                    this.clearClientSession(patchState);
                    return of(null);
                })
            );
    }
	
    @Action(RESET_QUERY_PARAMS)
    public clearQueryParams({patchState}): StateContext<UserModel> {
        return patchState({
            queryParams: null
        });
    }

	@Action(POPULATE_POLICY_MANAGEMENT)
	public populatePolicyManagement({patchState, getState}): StateContext<UserModel> {
		const state = getState();
		const menuPolicyManagement = this.extractPolicyManagementMenu(state.user);
		return patchState({
			menu: menuPolicyManagement
		});
	}
	
	@Action(BACK_TO_ALPHA)
	public backToAlpha(): void {
	  window.location.href = environment.alpha_url;
	}
  
	@Action(BACK_TO_SAT)
	public backToSAT(
		{ patchState, getState }: StateContext<UserModel>) {
	  const state = getState();
	  return this.userService.backToSAT(state.user.userId).pipe(
		map((res) => {
		  console.log('BACK TO SAT', res);
		  if (res.redirectUrl) {
			window.location.href = res.redirectUrl;
		  }
		}),
		catchError((err) => {
		  return null;
		})
	  );
	}
	
	@Action(REDIRECT_SAT)
	public redirectSAT({ patchState }: StateContext<UserModel>, { payload }: REDIRECT_SAT) {
		console.log("check:",payload)
	  return this.userService.redirectSAT(payload).pipe(
		map((res) => {
		  if (res.redirectUrl) {
			window.location.href = res.redirectUrl;
		  }
		}),
		catchError((err) => {
		  return null;
		})
	  );
	}

    @Action(SIGN_OUT)
    public signOut(
        { patchState, getState }: StateContext<UserModel>) {
        const state = getState();
        if (!state.user?.userId) {
            this.clearClientSession(patchState);
            return of(null);
        }

        return this.userService.signOut(state.user.userId)
            .pipe(
                map(() => {
                    this.clearClientSession(patchState);
                    this.authSessionFacade.removeSharedSatSession();
                    this.store.dispatch(new RESET_SAT_MOTOR_STATE());
                    window.location.href = '/login';
                }),
                catchError(err => {
                    this.clearClientSession(patchState);
                    this.authSessionFacade.removeSharedSatSession();
                    window.location.href = '/login';
                    return of(null);
                })
            );
    }

    @Action(CLEAR_AUTH_SESSION)
    public clearAuthSession(
      { patchState }: StateContext<UserModel>,
    ): void {
      this.clearClientSession(patchState);
      this.store.dispatch(new RESET_SAT_MOTOR_STATE());
    }

    getUserRole(state) {
        if (state) {
            if (state.admin) {
                return USERROLE.ADMIN;
            }

            if (state.agent) {
                return USERROLE.AGENT;
            }

            if (state.branchAdmin) {
                return USERROLE.BRANCH;
            }

            if (state.callCentreOrBancaAgent) {
                return USERROLE.BANCA;
            }
        }
        return null;
    }

    @Action(STORE_QUERY_PARAMS)
    public storeQueryParamsContractId({ patchState, getState }, { payload }: STORE_QUERY_PARAMS): StateContext<UserModel> {
      const state = getState();
      if (state.user?.userId) {
        this.authSessionFacade.setSession(state.user, payload ?? {});
      }
      return patchState({
        queryParams: payload,
      });
    }

    @Action(RESTORE_AUTH_SESSION)
    public restoreAuthSession(
      { getState, patchState }: StateContext<UserModel>,
      { payload }: RESTORE_AUTH_SESSION) {
      const state = getState();
      const user = state.user;
      if (!user?.userId) {
        this.clearClientSession(patchState);
        return;
      }

      const queryParams = payload?.queryParams ?? state.queryParams ?? {};
      this.authSessionFacade.setSession(user, queryParams);
      if (!this.authSessionFacade.hasSession()) {
        this.clearClientSession(patchState);
        return;
      }

      patchState({
        user,
        loading: false,
        role: this.getUserRole(user),
        emailConfig: null,
        queryParams,
        menu: this.extractPolicyManagementMenu(user)
      });
    }

    @Action(REDIRECT_ALPHA_CCENTRIC)
    public redirectAlphaCCentric({ patchState }: StateContext<UserModel>, { payload }: REDIRECT_SAT) {
      console.log("check:",payload)
      return this.userService.redirectToAlphaCCentric(payload).pipe(
      map((res) => {
        if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
        }
      }),
      catchError((err) => {
        return null;
      })
      );
    }

    @Action(CROSS_SELL_ISSUE_NEW_REDIRECT_TO_ALPHA)
    public crossSellIssueNewRedirectToAlpha({ patchState, getState }: StateContext<UserModel>, { payload }: CROSS_SELL_ISSUE_NEW_REDIRECT_TO_ALPHA) {
      const state = getState();
      return this.userService.crossSellIssueNewRedirectToAlpha(payload).pipe(
      map((res) => {
        if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
        }
      }),
      catchError((err) => {
        return null;
      })
      );
    }

    private clearClientSession(
      patchState: StateContext<UserModel>['patchState']
    ): void {
      this.authSessionFacade.clearSession();
      patchState({
        ...defaultUserState
      });
    }

    private extractPolicyManagementMenu(user: Partial<UserResponse>): any {
      const menuList = user?.menuList ?? [];
      return (
        menuList.find(
          (element: any) =>
            element.menuId == 'SA_MNPOLICIES' || element.menuId == 'MNPOLICIES'
        ) ?? null
      );
    }
}


